import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router'
import { FaIconComponent } from '@fortawesome/angular-fontawesome'
import { faClock, faGear } from '@fortawesome/free-solid-svg-icons'
import { filter, of } from 'rxjs'
import { catchError } from 'rxjs/operators'

import { AuthenticationService, BaseComponent, FeatureType, ModalSize } from '../shared'
import { isPublicSwpppPath } from '../shared/utility/public-anonymous-path'
import { AppearanceSettingsComponent } from '../shared/modals/appearance-settings.component'
import { ThemeService } from '../shared/theme.service'

interface CacheDatesResponse {
    cacheCreatedDate?: string
    cacheExpirationDate?: string
}

@Component({
    selector: 'app-nav-bar',
    templateUrl: './nav-bar.component.html',
    standalone: true,
    imports: [RouterLink, RouterLinkActive, FaIconComponent],
})
export class NavBarComponent extends BaseComponent implements OnInit {
    router = inject(Router)
    authentication = inject(AuthenticationService)
    featureType = FeatureType
    theme = inject(ThemeService)
    private destroyRef = inject(DestroyRef)

    faClock = faClock
    faGear = faGear
    cacheExpirationDate = signal<Date | null>(null)

    getCacheRefreshTooltip(exp: Date): string {
        if (exp.getTime() < Date.now()) {
            return 'Cache refreshes on next use'
        }
        const time = exp.toLocaleTimeString('en-US', {
            hour: 'numeric',
            minute: '2-digit',
            hour12: true,
        })
        return 'Cache refreshes at ' + time
    }

    loadCacheExpiration() {
        this.services.data
            .get<CacheDatesResponse>('/api/Action/GetLatestCacheDate')
            .pipe(catchError(() => of(null)))
            .subscribe(dto => {
                if (dto?.cacheExpirationDate) {
                    this.cacheExpirationDate.set(new Date(dto.cacheExpirationDate))
                }
            })
    }

    ngOnInit() {
        // Listen for navigation events and close the mobile menu
        this.router.events
            .pipe(
                filter(event => event instanceof NavigationEnd),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe(() => this.closeMobileMenu())
    }

    closeMobileMenu() {
        // Get the collapse element
        const collapseElement = document.getElementById('navbarSupportedContent')
        if (!collapseElement) {
            return
        }

        // Try to use Bootstrap's Collapse API if available
        let collapseInstance: any = null
        const bootstrap = (window as any).bootstrap
        if (bootstrap?.Collapse) {
            collapseInstance = bootstrap.Collapse.getInstance(collapseElement)
            if (!collapseInstance) {
                // Create a new instance if one doesn't exist
                collapseInstance = new bootstrap.Collapse(collapseElement, { toggle: false })
            }
        }

        // Close the collapse if it's open
        if (collapseInstance) {
            // Use Bootstrap API
            if (collapseElement.classList.contains('show')) {
                collapseInstance.hide()
            }
        } else {
            // Fallback: manually remove the 'show' class
            if (collapseElement.classList.contains('show')) {
                collapseElement.classList.remove('show')
                // Update the toggle button aria-expanded state
                const toggleButton = document.querySelector('[data-bs-target="#navbarSupportedContent"]') as HTMLElement
                if (toggleButton) {
                    toggleButton.setAttribute('aria-expanded', 'false')
                    toggleButton.classList.remove('collapsed')
                }
            }
        }
    }

    openAppearance() {
        this.services.modals.open(AppearanceSettingsComponent, ModalSize.small)
    }

    logout() {
        this.authentication.logout()
    }

    isPublicSwpppPage(): boolean {
        return isPublicSwpppPath(this.router.url) || isPublicSwpppPath()
    }

    canViewNavLink(...rights: string[]): boolean {
        return this.services.authentication.hasFeature(rights)
    }

    isActive(route: string): boolean {
        return this.router.url.startsWith('/' + route)
    }
}
